//codex-- Share acknowledged changes between settings and desktop/mobile menus; discard stale account reads.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useUserData } from './UserData';
import { bookmarkError, bookmarkRequest } from '../utils/bookmarks';
const Context = createContext(null);
const empty = { personal: [], company: [], canManageCompany: false };
const sort = (items) => [...items].sort((a, b) => a.label.localeCompare(b.label) || a.id - b.id);
export function BookmarksProvider({ children }) {
  const user = useUserData();
  const owner = String(user?.id || user?.users?.id || '');
  const apiUrl = process.env.REACT_APP_API_URL;
  const currentOwner = useRef(owner);
  currentOwner.current = owner;
  const sequence = useRef(0);
  const [snapshot, setSnapshot] = useState({ owner: '', ...empty });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const refresh = useCallback(async () => {
    if (!owner) return;
    const request = ++sequence.current;
    setLoading(true); setError('');
    try {
      const result = await bookmarkRequest(apiUrl, 'getList');
      if (request === sequence.current && currentOwner.current === owner) {
        setSnapshot({ owner, personal: result.personal, company: result.company, canManageCompany: result.can_manage_company === true });
      }
    } catch (failure) {
      if (request === sequence.current && currentOwner.current === owner) setError(bookmarkError(failure));
    } finally {
      if (request === sequence.current && currentOwner.current === owner) setLoading(false);
    }
  }, [apiUrl, owner]);
  useEffect(() => {
    setSnapshot({ owner, ...empty }); setError(''); setLoading(false); refresh();
    return () => { sequence.current += 1; };
  }, [owner, refresh]);
  const mutate = async (action, data, id = null) => {
    const result = await bookmarkRequest(apiUrl, action, data, id);
    if (currentOwner.current !== owner) return;
    //codex-- A read begun before an acknowledged write must not restore stale links.
    sequence.current += 1; setLoading(false); setError('');
    setSnapshot((previous) => {
      if (previous.owner !== owner) return previous;
      const items = previous[data.scope].filter((item) => String(item.id) !== String(id));
      if (action !== 'deleteBookmark') items.push(result.bookmark);
      return { ...previous, [data.scope]: sort(items) };
    });
  };
  const visible = snapshot.owner === owner ? snapshot : empty;
  return <Context.Provider value={{ ...visible, loading, error, refresh, mutate }}>{children}</Context.Provider>;
}
export const useBookmarks = () => useContext(Context);
